import {
  AfterViewInit,
  Component,
  DestroyRef,
  inject,
  signal,
  ViewChild,
  WritableSignal,
  ChangeDetectionStrategy,
  forwardRef,
} from '@angular/core';

import { LoggerService } from '@services/logger.service';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  SuiDialogConfig,
  DialogCloseOptions,
  DialogCloseReason,
  injectDirtyCloseHandler,
  SuiDialogSimpleComponent,
  SuiDialogHeaderSlot,
  SuiDialogContentSlot,
  SuiDialogFooterSlot,
} from '@sui/dialog';
import {
  faDownload,
  faEnvelope,
  faPaperPlane,
  faShare,
  faTimes,
} from '@fortawesome/free-solid-svg-icons';

import { SidebarService } from 'src/app/core/services/sidebar.service';

import { ExportSettingsComponent } from '../../../shared/components/export-settings/export-settings.component';

import { NomExportTransportTypes } from 'src/app/core/constants';
import { SimpleDialogBase } from 'src/app/shared/abstract/simple-dialog-base';

import { filter, take, tap } from 'rxjs';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { SuiButtonComponent } from '@sui/button';
import { TranslatePipe } from '@ngx-translate/core';

// * Share mode - determimes what is shared, single post, selected posts or resultset
export enum ShareMode {
  SINGLE = 'single',
  SELECTED = 'selected',
  RESULTSET = 'resultset',
}

export interface ShareDialogData {
  shareMode: ShareMode;
  objectId: string;
  objectClassId: number;
  postIds: string[];
  title?: string;
  shareParams: any;
}

export interface ShareDialogOutput {
  reason: DialogCloseReason;
}

@Component({
  selector: 'app-share-dialog',
  templateUrl: './share-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    SuiDialogSimpleComponent,
    SuiDialogHeaderSlot,
    FaIconComponent,
    SuiButtonComponent,
    SuiDialogContentSlot,
    forwardRef(() => ExportSettingsComponent),
    SuiDialogFooterSlot,
    TranslatePipe,
  ],
})
export class ShareDialogComponent
  extends SimpleDialogBase<ShareDialogData, ShareDialogOutput>
  implements AfterViewInit
{
  static dialogConfig: Partial<SuiDialogConfig> = {
    width: '25%',
    minWidth: 415,
    maxWidth: 1020,
  };
  @ViewChild('exportSettings', { static: false })
  exportSettingsComponent!: ExportSettingsComponent;

  private logger = inject(LoggerService);
  private destroyRef = inject(DestroyRef);
  private sidebarService = inject(SidebarService);
  protected dirtyHandler = injectDirtyCloseHandler(this.dialogRef);

  isDirty: WritableSignal<boolean> = signal(false);
  isValid: WritableSignal<boolean> = signal(true);

  public formData: any = {};
  public ShareMode = ShareMode;
  faShare = faShare;
  faTimes = faTimes;

  submitButtonText: string = this.translateService.instant('btn.share');
  submitButtonIcon: IconProp = faShare;
  objectActionTitle: string = this.translateService.instant(
    'share-dialog.title.share-content',
  );
  objectActionIcon: IconProp = faShare;

  get shareMode(): ShareMode {
    return this.dialogData.shareMode;
  }
  get objectId(): string {
    return this.dialogData.objectId;
  }
  get objectClassId(): number {
    return this.dialogData.objectClassId;
  }
  get postIds(): string[] {
    return this.dialogData.postIds;
  }
  get title(): string {
    return this.dialogData.title;
  }

  get shareParams(): any {
    return this.dialogData.shareParams;
  }

  ngOnInit(): void {
    // Only close the sidebar if ExportComponent is currently shown
    this.sidebarService.selectedComponent$
      .pipe(
        take(1),
        tap((component) => {
          if (component === 'ExportComponent') {
            this.sidebarService.closeSidebar();
          }
        }),
      )
      .subscribe();
  }

  ngAfterViewInit(): void {
    this.setupDirtyHandler(this.isDirty, this.isValid);
    // * a refused share lands in the band; the dialog already closes on success$ only (WAP-1976)
    this.exportSettingsComponent.dialogInlineError = this.showApiError;
  }

  exportFormatChanged(exportFormat: NomExportTransportTypes) {
    this.logger.debug('Export format changed', exportFormat);

    const configMap = {
      [NomExportTransportTypes.FILE]: {
        translationKey: 'btn.download',
        icon: faDownload,
      },
      [NomExportTransportTypes.EMAIL]: {
        translationKey: 'btn.send',
        icon: faEnvelope,
      },
      [NomExportTransportTypes.MATTERMOST]: {
        translationKey: 'btn.send',
        icon: faPaperPlane,
      },
    };

    const config = configMap[exportFormat] || {
      translationKey: 'btn.share',
      icon: faPaperPlane,
    };

    this.submitButtonText = this.translateService.instant(
      config.translationKey,
    );
    this.submitButtonIcon = config.icon;
  }

  exportSettingsChanged(exportSettings: any) {
    this.logger.debug('Export settings changed', exportSettings);
    this.formData = exportSettings;
  }

  handleIsDirtyChange(value: boolean) {
    this.isDirty.set(value);
  }

  handleIsValidChange(value: boolean) {
    this.isValid.set(value);
  }

  share() {
    this.exportSettingsComponent.success$
      .pipe(
        filter((success) => success === true),
        tap(() => {
          this.close({ reason: DialogCloseReason.Confirm });
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    // TODO: form NgRx state should be checked currently commented to avoid duplicate toast: raw Error object is being shown as “[object Object]”
    // this.exportSettingsComponent.error$
    //   .pipe(
    //     filter((error) => !!error),
    //     tap((error) => this.toastService.showError(error)),
    //     takeUntilDestroyed(this.destroyRef),
    //   )
    //   .subscribe();

    this.performSave();
  }

  protected override performSave(): void | DialogCloseOptions<ShareDialogOutput> {
    this.exportSettingsComponent.submit();
  }

  async cancel(): Promise<void> {
    await this.dirtyHandler.tryClose({
      reason: DialogCloseReason.Cancel,
    });
  }
}
